/**
 * PROD-227 PR6 — PendingChargesPill.
 *
 * Small pill in the CafeteriaWorkflowHeader ("3 pending charges",
 * design pass 00 §5) counting queued-offline POS sales only — serve
 * rows belong to the ConnectivityChip's total. Polls Dexie on the
 * chip's 2s cadence (same trade-off: polling beats a live-query
 * subscription for a status indicator); hidden at 0.
 *
 * When the count drains to 0 (the ReplayManager landed every queued
 * sale), the pill invalidates the POS student-context queries so the
 * next lookup renders server-authoritative balances instead of the
 * offline advisory values.
 */

import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'

import { countPendingMutationsByOperation } from '@/cafeteria-db/sync-queue'

const POLL_MS = 2_000

export function PendingChargesPill() {
  const qc = useQueryClient()
  const [count, setCount] = useState(0)
  const previousRef = useRef(0)

  useEffect(() => {
    let active = true
    const refresh = async () => {
      const pending = await countPendingMutationsByOperation('posSale')
      if (!active) {
        return
      }
      if (previousRef.current > 0 && pending === 0) {
        // Queue drained — cached advisory balances are stale; pull the
        // server-authoritative context on next render.
        void qc.invalidateQueries({ queryKey: ['cafeteria', 'pos-student-context'] })
      }
      previousRef.current = pending
      setCount(pending)
    }
    void refresh()
    const interval = window.setInterval(() => {
      void refresh()
    }, POLL_MS)
    return () => {
      active = false
      window.clearInterval(interval)
    }
  }, [qc])

  if (count === 0) {
    return null
  }

  return (
    <span
      data-testid="pending-charges-pill"
      className="inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-3 py-1 text-xs font-medium text-amber-900">
      <span aria-hidden className="h-2 w-2 rounded-full bg-amber-500" />
      {count} pending {count === 1 ? 'charge' : 'charges'}
    </span>
  )
}
